import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import apiClient from "services/ApiClient";
import { apiCheckpointById } from "helpers/url/api/v2";
import { useReminderRows } from "features/SendMessage/hooks";
import type { NewHireJourney } from "models/v2/newHireJourney";
import type { ReminderRow } from "features/SendMessage/types";
import type { Checkpoint, CheckpointSummary } from "features/Resources/Checkpoint/models";
import checkpointsByJourneyBatcher from "./checkpointsByJourneyBatcher";

export const checkpointKeys = {
  all: ["checkpoints"] as const,
  lists: () => [...checkpointKeys.all, "list"] as const,
  byJourney: (journeyId: string) => [...checkpointKeys.lists(), { journeyId }] as const,
  details: () => [...checkpointKeys.all, "detail"] as const,
  detail: (id: string) => [...checkpointKeys.details(), id] as const,
};

export const useCheckpointDetailQuery = (id: string | undefined, options: { enabled?: boolean } = {}) =>
  useQuery<Checkpoint>({
    queryKey: checkpointKeys.detail(id as string),
    queryFn: async () => apiClient.get<Checkpoint>(apiCheckpointById(id as string)),
    staleTime: 1000 * 60 * 3,
    enabled: !!id && (options.enabled ?? true),
    ...options,
  });

export const useCheckpointsByJourneyQuery = (journeyId: string, options: { enabled?: boolean } = {}) =>
  useQuery<CheckpointSummary[]>({
    queryKey: checkpointKeys.byJourney(journeyId),
    queryFn: async () => checkpointsByJourneyBatcher.fetch({ newHireJourneyId: journeyId }),
    staleTime: 1000 * 60 * 3,
    enabled: !!journeyId && (options.enabled ?? true),
    ...options,
  });

export function useNotifyCheckpoint(id: string | undefined) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => apiClient.post(`${apiCheckpointById(id as string)}/notify`, {}),
    onSuccess: () => {
      toast({ title: "Notification sent!", status: "success" });
      queryClient.invalidateQueries({ queryKey: checkpointKeys.details() });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send notification.", status: "error" });
    },
  });
}

export function useRetryNotification(checkpointId: string | undefined) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (notificationId: number) =>
      apiClient.post(`${apiCheckpointById(checkpointId as string)}/notify`, { notificationId }),
    onSuccess: () => {
      toast({ title: "Notification sent!", status: "success" });
      queryClient.invalidateQueries({ queryKey: checkpointKeys.details() });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to retry notification.", status: "error" });
    },
  });
}

export function useCheckpointStakeholderGroups(
  newHireJourney: NewHireJourney | undefined,
  results: { tasks?: { id: number }[] } | undefined,
  options?: { enabled?: boolean; currentUserId?: string },
): { rows: ReminderRow[]; youRow?: ReminderRow; isLoading: boolean } {
  const filterTaskIds = useMemo(
    () => (results?.tasks?.length ? new Set(results.tasks.map((t) => t.id)) : undefined),
    [results],
  );

  const { rows, youRow, isLoading } = useReminderRows(newHireJourney as NewHireJourney, {
    enabled: (options?.enabled ?? true) && !!newHireJourney?.id && !!filterTaskIds,
    currentUserId: options?.currentUserId,
    filterTaskIds,
  });

  return { rows, youRow, isLoading };
}
